import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw, Lightbulb, ArrowLeft, ArrowRight, Check, Loader2, ImageIcon, ZoomIn, ShoppingBag, Clapperboard, Pencil, Save, X, Volume2, FileText, Sparkles, Type, Plus, ImagePlus } from "lucide-react";
import api, { imageUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import StaircaseLoader from "@/components/StaircaseLoader";
import { NarrationMeter, splitNarration, isOverNarration } from "@/components/NarrationMeter";

// Resize an uploaded image to a data URL before upload (keeps files small for the proxy).
function resizeToDataUrl(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const r = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Clean the AI explanation of all markdown (stars, asterisks, headers, bullets)
// and split into readable, well-spaced lines.
function formatExplain(text) {
  return (text || "")
    .replace(/\r/g, "")
    .replace(/\*\*/g, "")
    .replace(/[*_`#>]/g, "")
    .split(/\n+/)
    .map((l) => l.replace(/^\s*[-•·]\s*/, "").trim())
    .filter(Boolean);
}


export default function StepViewer() {
  const { id, index } = useParams();
  const idx = parseInt(index, 10);
  const navigate = useNavigate();
  const { user } = useAuth();
  const autoImages = user?.settings?.step_illustrations !== false;
  const readAloud = user?.settings?.voice_over === true;

  const [manual, setManual] = useState(null);
  const [imgId, setImgId] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [enlarge, setEnlarge] = useState(false);
  const [help, setHelp] = useState(null); // {type, text}
  const [helpLoading, setHelpLoading] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftWritten, setDraftWritten] = useState("");
  const [draftVoice, setDraftVoice] = useState("");
  const [rewriteInstr, setRewriteInstr] = useState("");
  const [rewriting, setRewriting] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [insertOpen, setInsertOpen] = useState(false);
  const [insImg, setInsImg] = useState(null);
  const [insText, setInsText] = useState("");
  const [insBusy, setInsBusy] = useState(false);

  const onPickInsertImg = async (file) => {
    if (!file) return;
    try { setInsImg(await resizeToDataUrl(file)); }
    catch { toast.error("Couldn't read that image"); }
  };
  const saveInsert = async () => {
    if (!insImg) { toast.error("Add a photo for the new step"); return; }
    if (!insText.trim()) { toast.error("Describe the new step in your own words"); return; }
    if (isOverNarration(insText)) { toast.error("Keep it under ~42 words (about 16 seconds)"); return; }
    setInsBusy(true);
    try {
      const { data } = await api.post(`/manuals/${id}/steps/insert`, { after_index: idx, text: insText.trim(), image: insImg });
      toast.success("New step inserted 🎬");
      setInsertOpen(false);
      navigate(`/manual/${id}/step/${data.new_index}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't insert the step");
    } finally { setInsBusy(false); }
  };
  const audioRef = useRef(null);
  const playToken = useRef(0);
  const explainCache = useRef({});

  const step = manual?.steps?.[idx];
  const total = manual?.steps?.length || 0;

  const stopAudio = useCallback(() => {
    playToken.current += 1; // invalidate any in-flight speech request
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  const speak = useCallback(async () => {
    stopAudio();
    const token = playToken.current;
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${idx}/speech`);
      if (token !== playToken.current) return; // stopped/muted while generating
      const audio = new Audio(`${API}/audio/${data.audio_id}`);
      audioRef.current = audio;
      await audio.play();
      if (token !== playToken.current) audio.pause(); // stopped during playback start
    } catch (e) {
      /* autoplay blocked or generation failed — silently skip */
    }
  }, [id, idx, stopAudio]);

  // Read arbitrary text aloud. Strips markdown.
  const speakText = useCallback(async (text) => {
    const clean = (text || "")
      .replace(/[*_#`>]/g, " ")
      .replace(/^\s*[-•]\s*/gm, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!clean) return;
    stopAudio();
    const token = playToken.current;
    try {
      const { data } = await api.post(`/tts`, { text: clean.slice(0, 3400) });
      if (token !== playToken.current) return;
      const audio = new Audio(`${API}/audio/${data.audio_id}`);
      audioRef.current = audio;
      await audio.play();
      if (token !== playToken.current) audio.pause();
    } catch (e) {
      /* autoplay blocked or generation failed — silently skip */
    }
  }, [stopAudio]);
  void speakText;


  const loadImage = useCallback(async () => {
    setImgLoading(true);
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${idx}/image`);
      setImgId(data.image_id);
    } catch {
      toast.error("Couldn't load the illustration");
    } finally {
      setImgLoading(false);
    }
  }, [id, idx]);

  useEffect(() => {
    setHelp(null);
    setEnlarge(false);
    setEditing(false);
    stopAudio();
    // Auto-load the in-depth "Explain" content natively for this step.
    const cacheKey = `${id}:${idx}`;
    if (explainCache.current[cacheKey]) {
      setHelp({ type: "explain", text: explainCache.current[cacheKey] });
    } else {
      setHelpLoading("explain");
      api.post(`/manuals/${id}/steps/${idx}/explain`)
        .then(({ data }) => {
          explainCache.current[cacheKey] = data.text;
          setHelp({ type: "explain", text: data.text });
        })
        .catch(() => {})
        .finally(() => setHelpLoading(null));
    }
    api.get(`/manuals/${id}`).then(({ data }) => {
      setManual(data);
      const existing = data.steps?.[idx]?.thumbnail_id || null;
      setImgId(existing);
      const starNoSteps = data.moviestar && data.moviestar_step_photos === false;
      if (!existing && user?.settings?.step_illustrations !== false && !starNoSteps) {
        // auto-generate
        setImgLoading(true);
        api.post(`/manuals/${id}/steps/${idx}/image`)
          .then(({ data: d }) => setImgId(d.image_id))
          .catch(() => {})
          .finally(() => setImgLoading(false));
      }
      if (readAloud) speak();
    }).catch(() => navigate("/"));
    return () => stopAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, idx]);

  // Stop playback immediately when read-aloud is turned off in Settings.
  useEffect(() => {
    if (!readAloud) stopAudio();
  }, [readAloud, stopAudio]);

  const done = async () => {
    if (idx + 1 < total) {
      navigate(`/manual/${id}/step/${idx + 1}`);
    } else {
      setFinishing(true);
      try {
        await api.post(`/manuals/${id}/complete`);
        navigate(`/manual/${id}/complete`);
      } catch {
        navigate(`/manual/${id}/complete`);
      }
    }
  };
  const back = () => (idx === 0 ? navigate(`/manual/${id}`) : navigate(`/manual/${id}/step/${idx - 1}`));

  // Quick jump straight to the "Done" page (video settings) — does NOT render the video.
  const makeVideoNow = async () => {
    setFinishing(true);
    try {
      await api.post(`/manuals/${id}/complete`);
    } catch {
      /* still go to the done page */
    }
    navigate(`/manual/${id}/complete`);
  };

  const openEdit = () => {
    const raw = help?.text || step?.explanation || "";
    setDraftTitle(step?.title || "");
    setDraftWritten(formatExplain(raw).join("\n\n"));
    setDraftVoice(step?.narration || step?.title || "");
    setRewriteInstr("");
    setEditing(true);
  };

  const rewriteField = async (target) => {
    setRewriting(target);
    try {
      const { data } = await api.post(`/manuals/${id}/steps/${idx}/rewrite`, {
        target,
        instruction: rewriteInstr.trim() || undefined,
      });
      if (target === "title") setDraftTitle(data.text);
      else if (target === "voice") setDraftVoice(data.text);
      else setDraftWritten(data.text);
      toast.success("AI drafted a new version — review it, then Save");
    } catch {
      toast.error("Couldn't rewrite that — please try again");
    } finally {
      setRewriting(null);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/manuals/${id}/steps/${idx}/content`, {
        title: draftTitle,
        explanation: draftWritten,
        narration: draftVoice,
      });
      const cacheKey = `${id}:${idx}`;
      explainCache.current[cacheKey] = draftWritten;
      setHelp({ type: "explain", text: draftWritten });
      setManual((m) => {
        if (!m) return m;
        const steps = [...m.steps];
        steps[idx] = { ...steps[idx], title: draftTitle.trim() || steps[idx].title, explanation: draftWritten, narration: draftVoice, edited: true };
        return { ...m, steps, has_edits: true };
      });
      setEditing(false);
      toast.success("Saved — your PDF and video will use your version");
    } catch {
      toast.error("Couldn't save your edits — please try again");
    } finally {
      setSavingEdit(false);
    }
  };

  if (!manual || !step) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="spin text-[#5d8a52]" />
      </div>
    );
  }

  const progress = ((idx + 1) / total) * 100;

  return (
    <div className="max-w-2xl mx-auto px-4 pb-16">
      <div className="flex items-center justify-between pt-5 pb-3">
        <span className="font-bold text-[#9a96ac]" data-testid="step-progress-label">
          Step {idx + 1} of {total}
        </span>
        <button
          data-testid="restart-btn"
          onClick={() => navigate(`/manual/${id}`)}
          className="flex items-center gap-1.5 text-[#9a96ac] font-semibold hover:text-[#5d8a52] transition-colors"
        >
          <RotateCcw size={15} /> Restart
        </button>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[#e9e5f5] overflow-hidden mb-6">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${progress}%`, background: "linear-gradient(90deg,#a9c39c,#5d8a52)" }} />
      </div>

      <div className="sms-card p-6 sms-fade-up" key={idx} style={{ border: "2px solid #5d8a52", background: "#fff" }}>
        {/* top action grid — two rows on mobile so nothing slips off the edge */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            data-testid="back-step-btn"
            onClick={back}
            className="flex items-center justify-center gap-1.5 font-bold text-[#6b6880] bg-white border border-[#e3e0ee] rounded-xl px-3 py-2 text-sm hover:bg-[#faf9fd] transition-colors whitespace-nowrap"
          >
            <ArrowLeft size={15} /> Back
          </button>
          {manual?.diy ? (
            <button
              data-testid="insert-step-btn"
              onClick={() => { setInsImg(null); setInsText(""); setInsertOpen(true); }}
              className="flex items-center justify-center gap-1.5 font-bold text-[#5d8a52] bg-white border border-[#5d8a52] rounded-xl px-3 py-2 text-sm hover:bg-[#f6f3ff] transition-colors whitespace-nowrap"
            >
              <Plus size={15} /> Insert New Step Next
            </button>
          ) : (
            <button
              data-testid="shop-supplies-step-btn"
              onClick={() => navigate(`/manual/${id}/shop`)}
              className="flex items-center justify-center gap-1.5 font-bold text-[#5d8a52] bg-white border border-[#5d8a52] rounded-xl px-3 py-2 text-sm hover:bg-[#f6f3ff] transition-colors whitespace-nowrap"
            >
              <ShoppingBag size={15} /> Shop Supplies
            </button>
          )}
          {idx + 1 < total && (
            <button
              data-testid="make-video-now-btn"
              onClick={makeVideoNow}
              disabled={finishing}
              title="Skip ahead to the finish page and make your video"
              className="flex items-center justify-center gap-1.5 font-bold text-white bg-[#dc2626] border border-[#dc2626] rounded-xl px-3 py-2 text-sm hover:bg-[#b91c1c] transition-colors whitespace-nowrap disabled:opacity-70"
            >
              <Clapperboard size={15} /> Make Video Now
            </button>
          )}
          <button
            data-testid="done-step-btn"
            onClick={done}
            disabled={finishing}
            className={`sms-solid-btn ${idx + 1 < total ? "" : "col-span-2"} flex items-center justify-center gap-1.5 text-white font-bold rounded-xl px-4 py-2 text-sm disabled:opacity-70`}
          >
            {finishing ? (
              <Loader2 size={16} className="spin" />
            ) : idx + 1 < total ? (
              <>Next Step <ArrowRight size={16} strokeWidth={3} /></>
            ) : (
              <><Check size={16} strokeWidth={3} /> Done</>
            )}
          </button>
        </div>

        <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-extrabold text-xl mb-5"
          style={{ background: "linear-gradient(160deg,#7ba46f,#4f7746)", boxShadow: "0 10px 22px -10px rgba(79, 119, 70,.7)" }}>
          {idx + 1}
        </div>

        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl md:text-3xl font-extrabold leading-tight" data-testid="step-title">
            {step.title}
            {step.edited && <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wide text-[#4f7746] bg-[#eef3ea] rounded-full px-2 py-0.5" data-testid="step-edited-badge">Edited</span>}
          </h1>
          {!editing && (
            <button
              data-testid="edit-step-btn"
              onClick={openEdit}
              title="Edit this step's written & voice script"
              className="shrink-0 flex items-center gap-1.5 font-bold text-[#4f7746] bg-white border-2 border-[#5d8a52] rounded-xl px-3 py-1.5 text-sm hover:bg-[#eef3ea] transition-colors"
            >
              <Pencil size={14} /> Edit
            </button>
          )}
        </div>

        {/* Per-step script editor (owner) */}
        {editing && (
          <div className="mt-5 rounded-2xl border-2 p-5 sms-fade-up" style={{ borderColor: "#5d8a52", background: "#fbfdfa" }} data-testid="step-editor">
            {/* shared AI guidance */}
            <div className="rounded-xl bg-[#eef3ea] p-3 mb-4">
              <label className="flex items-center gap-1.5 font-bold text-[#171426] text-xs">
                <Sparkles size={13} className="text-[#4f7746]" /> Guide the AI rewrites (optional)
              </label>
              <input
                data-testid="rewrite-instruction-input"
                value={rewriteInstr}
                onChange={(e) => setRewriteInstr(e.target.value)}
                placeholder="e.g. make it shorter, add more detail, friendlier tone"
                className="sms-input w-full px-3 py-2 text-sm mt-2"
              />
            </div>

            {/* Title */}
            <div>
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 font-bold text-[#171426] text-sm">
                  <Type size={15} className="text-[#4f7746]" /> Step title
                </label>
                <button
                  data-testid="rewrite-title-btn"
                  onClick={() => rewriteField("title")}
                  disabled={rewriting !== null}
                  className="flex items-center gap-1.5 font-bold text-[#4f7746] bg-white border border-[#5d8a52] rounded-lg px-2.5 py-1 text-xs hover:bg-[#eef3ea] transition-colors disabled:opacity-60"
                >
                  {rewriting === "title" ? <Loader2 size={12} className="spin" /> : <Sparkles size={12} />} Rewrite
                </button>
              </div>
              <input
                data-testid="edit-title-input"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                className="sms-input w-full px-3 py-2.5 text-sm font-semibold mt-2"
                placeholder="Step headline…"
              />
            </div>

            {/* Written script */}
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 font-bold text-[#171426] text-sm">
                  <FileText size={15} className="text-[#4f7746]" /> Written script
                </label>
                <button
                  data-testid="rewrite-written-btn"
                  onClick={() => rewriteField("written")}
                  disabled={rewriting !== null}
                  className="flex items-center gap-1.5 font-bold text-[#4f7746] bg-white border border-[#5d8a52] rounded-lg px-2.5 py-1 text-xs hover:bg-[#eef3ea] transition-colors disabled:opacity-60"
                >
                  {rewriting === "written" ? <Loader2 size={12} className="spin" /> : <Sparkles size={12} />} Rewrite with AI
                </button>
              </div>
              <p className="text-xs text-[#6b6880] mt-0.5 mb-2">What readers see on this page and in the downloadable PDF.</p>
              <textarea
                data-testid="edit-written-textarea"
                value={draftWritten}
                onChange={(e) => setDraftWritten(e.target.value)}
                rows={7}
                className="sms-input w-full px-3 py-2.5 text-sm leading-relaxed resize-y"
                placeholder="Write the on-screen explanation for this step…"
              />
            </div>

            {/* Voice script */}
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 font-bold text-[#171426] text-sm">
                  <Volume2 size={15} className="text-[#4f7746]" /> Voice script
                </label>
                <button
                  data-testid="rewrite-voice-btn"
                  onClick={() => rewriteField("voice")}
                  disabled={rewriting !== null}
                  className="flex items-center gap-1.5 font-bold text-[#4f7746] bg-white border border-[#5d8a52] rounded-lg px-2.5 py-1 text-xs hover:bg-[#eef3ea] transition-colors disabled:opacity-60"
                >
                  {rewriting === "voice" ? <Loader2 size={12} className="spin" /> : <Sparkles size={12} />} Rewrite with AI
                </button>
              </div>
              <p className="text-xs text-[#6b6880] mt-0.5 mb-2">The exact words spoken aloud in your video's narration.</p>
              <textarea
                data-testid="edit-voice-textarea"
                value={draftVoice}
                onChange={(e) => setDraftVoice(e.target.value)}
                rows={3}
                className="sms-input w-full px-3 py-2.5 text-sm leading-relaxed resize-y"
                placeholder="Write what the narrator should say for this step…"
              />
              <NarrationMeter text={draftVoice} />
              {isOverNarration(draftVoice) && (
                <button
                  type="button"
                  data-testid="trim-narration-btn"
                  onClick={() => {
                    const { kept, rest } = splitNarration(draftVoice);
                    setDraftVoice(kept);
                    if (rest) setDraftWritten((w) => (w && w.trim() ? `${w.trim()}\n\n${rest}` : rest));
                    toast.success("Trimmed to ~16s — the rest moved into your written steps (PDF).");
                  }}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#c2410c] bg-[#fdf3e0] border border-[#f0d190] rounded-full px-3 py-1.5 hover:bg-[#fbe6c2] transition-colors"
                >
                  <Sparkles size={13} /> Trim to 16s — move the rest to the PDF
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <button
                data-testid="cancel-edit-btn"
                onClick={() => setEditing(false)}
                disabled={savingEdit}
                className="flex items-center gap-1.5 font-bold text-[#6b6880] bg-white border border-[#e3e0ee] rounded-xl px-4 py-2 text-sm hover:bg-[#faf9fd] transition-colors disabled:opacity-60"
              >
                <X size={15} /> Cancel
              </button>
              <button
                data-testid="save-edit-btn"
                onClick={saveEdit}
                disabled={savingEdit}
                className="sms-solid-btn ml-auto flex items-center gap-1.5 text-white font-bold rounded-xl px-5 py-2 text-sm disabled:opacity-70"
              >
                {savingEdit ? <Loader2 size={15} className="spin" /> : <Save size={15} />} Save
              </button>
            </div>
          </div>
        )}

        {/* image area */}
        <div className="mt-5">
          {imgId ? (
            <div className="inline-flex flex-col items-start gap-2" data-testid="step-image-thumb">
              <img src={imageUrl(imgId)} alt="" className="w-24 h-24 rounded-2xl object-cover border border-[#ece9f5]" />
              <button
                data-testid="enlarge-image-btn"
                onClick={() => setEnlarge(true)}
                aria-label="Enlarge image"
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#5d8a52] bg-[#eef3ea] hover:bg-[#e0ecd9] transition-colors"
              >
                <ZoomIn size={18} />
              </button>
            </div>
          ) : imgLoading ? (
            <div className="w-24 h-24 rounded-2xl bg-[#eef3ea] flex items-center justify-center overflow-hidden" data-testid="step-image-loading">
              <StaircaseLoader width={66} />
            </div>
          ) : manual?.moviestar && manual?.moviestar_step_photos === false ? (
            <p className="text-xs text-[#9a96ac] leading-relaxed max-w-sm" data-testid="step-photos-off-note">
              Step photos are off for this manual. Turn on <span className="font-semibold text-[#5d8a52]">"Put me in every step photo"</span> before creating to include them next time.
            </p>
          ) : !autoImages ? (
            <button
              data-testid="show-image-btn"
              onClick={loadImage}
              className="inline-flex items-center gap-2 font-semibold text-[#5d8a52] border border-[#e0d8fb] rounded-2xl px-4 py-2.5 hover:bg-[#f6f3ff] transition-colors"
            >
              <ImageIcon size={17} /> Show image
            </button>
          ) : null}
        </div>

        {/* Why box */}
        {(step.why || step.tip) && (
          <div className="mt-5 rounded-2xl bg-[#eef3ea] p-5">
            {step.why && (
              <>
                <div className="flex items-center gap-2 font-bold text-[#4f7746]">
                  <Lightbulb size={17} /> Why?
                </div>
                <p className="text-[#5b4a91] mt-2 leading-relaxed">{step.why}</p>
              </>
            )}
            {step.tip && (
              <>
                <div className="h-px bg-[#e2d9fb] my-4" />
                <p className="text-[#5b4a91] leading-relaxed">
                  <span className="font-bold text-[#4f7746]">Tip: </span>{step.tip}
                </p>
              </>
            )}
          </div>
        )}

        {/* Native in-depth explanation */}
        {!editing && help?.text && (
          <div className="mt-5 rounded-2xl border border-[#ece9f5] bg-[#fbfaff] p-5 sms-fade-up" data-testid="help-result">
            <div className="flex items-center gap-2 font-bold text-[#171426] mb-3">
              <Lightbulb size={17} className="text-[#4f7746]" /> In depth
            </div>
            <div className="space-y-3" data-testid="explain-content">
              {formatExplain(help.text).map((line, i) => (
                <p key={i} className="text-[#3c3952] leading-relaxed">{line}</p>
              ))}
            </div>
            <div className="flex items-center mt-4">
              <button
                data-testid="back-step-btn-bottom"
                onClick={back}
                className="flex items-center justify-center gap-1.5 font-bold text-[#6b6880] bg-white border border-[#e3e0ee] rounded-xl px-3.5 py-2 text-sm hover:bg-[#faf9fd] transition-colors"
              >
                <ArrowLeft size={15} /> Back
              </button>
              <button
                data-testid="done-step-btn-bottom"
                onClick={done}
                disabled={finishing}
                className="sms-solid-btn ml-auto flex items-center justify-center gap-1.5 text-white font-bold rounded-xl px-4 py-2 text-sm disabled:opacity-70"
              >
                {finishing ? (
                  <Loader2 size={16} className="spin" />
                ) : idx + 1 < total ? (
                  <>Next Step <ArrowRight size={16} strokeWidth={3} /></>
                ) : (
                  <><Check size={16} strokeWidth={3} /> Done</>
                )}
              </button>
            </div>
          </div>
        )}
        {helpLoading === "explain" && !help && (
          <div className="mt-5 flex items-center gap-2 text-[#9a96ac] text-sm" data-testid="explain-loading">
            <Loader2 size={15} className="spin" /> Bringing in more detail…
          </div>
        )}
      </div>

      {enlarge && imgId && (
        <div
          className="fixed inset-0 z-50 bg-[#171426]/80 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setEnlarge(false)}
          data-testid="image-enlarge-overlay"
        >
          <img src={imageUrl(imgId)} alt="" className="max-w-full max-h-full rounded-3xl sms-fade-up" />
        </div>
      )}

      {insertOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => !insBusy && setInsertOpen(false)} data-testid="insert-step-overlay">
          <div onClick={(e) => e.stopPropagation()} className="sms-tweed w-full max-w-lg rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] text-white p-5 sms-fade-up" style={{ boxShadow: "0 30px 90px -24px rgba(0,0,0,.85), 0 0 44px -14px rgba(93,138,82,.45)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-extrabold text-lg flex items-center gap-2"><Plus size={18} className="text-[#8fbf82]" /> Insert a new step after Step {idx + 1}</div>
              <button onClick={() => !insBusy && setInsertOpen(false)} data-testid="insert-step-close" className="text-white/50 hover:text-white transition-colors"><X size={18} /></button>
            </div>
            <div className="sms-tweed rounded-2xl border-2 border-[#2a3a26] bg-[#0a0a0f] p-4">
              <div className="flex gap-4">
                <label data-testid="insert-step-photo-btn" className="w-28 h-28 shrink-0 rounded-xl border-2 border-dashed border-[#5d8a52] bg-black flex flex-col items-center justify-center cursor-pointer hover:bg-[#111] transition-colors overflow-hidden">
                  {insImg ? (
                    <img src={insImg} alt="new step" className="w-full h-full object-cover" />
                  ) : (
                    <><ImagePlus size={22} className="text-[#a9d79b]" /><span className="text-[10px] font-bold text-[#a9d79b] mt-1">Add photo</span></>
                  )}
                  <input type="file" accept="image/*" className="hidden" data-testid="insert-step-photo-input" onChange={(e) => onPickInsertImg(e.target.files?.[0])} disabled={insBusy} />
                </label>
                <div className="flex-1 min-w-0">
                  <textarea
                    data-testid="insert-step-text"
                    rows={4}
                    value={insText}
                    onChange={(e) => setInsText(e.target.value.slice(0, 600))}
                    placeholder="Describe your new step in your own words…"
                    className="w-full px-3 py-2 text-sm rounded-xl bg-black text-white placeholder:text-white/40 border-2 border-[#5d8a52] outline-none resize-none leading-snug"
                  />
                  <NarrationMeter text={insText} className="mt-1.5" />
                </div>
              </div>
            </div>
            <button data-testid="insert-step-save" onClick={saveInsert} disabled={insBusy} className="mt-4 w-full flex items-center justify-center gap-2 font-extrabold py-3 rounded-xl text-white bg-[#dc2626] hover:bg-[#b91c1c] transition-colors disabled:opacity-70">
              {insBusy ? <Loader2 size={18} className="spin" /> : <Check size={18} />} Insert Step
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
